import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
    Users,
    Plus,
    Search,
    Eye,
    Edit,
    Check,
    CheckCircle,
    AlertTriangle,
    Trash2,
    Loader2,
    Shield,
    Building2,
    RotateCw,
    Filter,
    FileText,
    X,
    XCircle,
    Clock,
    Info,
    Upload,
    History,
    ChevronUp,
    ChevronDown,
} from 'lucide-react';
import { apiClient, Driver, CreateDriverRequest, UpdateDriverRequest, Site, IssueSeverity, BreakdownCategoryDefinition, BulkOperationResultsResponse, RecentlyCheckedDriver, ConsentCategorizationPreviewResponse } from '../services/api';
import { iconComponents } from '../utils/ui-mappings';
import AddDriverModal from './Modals/AddDriverModal';
import DriverViewModal from './Modals/DriverViewModal';
import EditDriverModal from './Modals/EditDriverModal';
import LicenceCheckModal from './Modals/LicenceCheckModal';
import ConsentDetailsModal from './Modals/ConsentDetailsModal';
import BulkCheckResultsModal from './Modals/BulkCheckResultsModal';
import ConsentWarningModal from './Modals/ConsentWarningModal';
import RecentCheckWarningModal from './Modals/RecentCheckWarningModal';
import ConsentCategorizationModal from './Modals/ConsentCategorizationModal';
import SelectionToolbar, { QuickSelectGroup } from './SelectionToolbar';
import CSVImportModal from './Modals/CSVImportModal';
import DriversHistoryModal from './Modals/DriversHistoryModal';
import { hasValidConsent } from '../utils/driver-helpers';
import { useSort } from '../hooks/useSort';

// Issue filter type - dynamic (accepts any backend issue code)
type IssueFilterType = string | null;

interface DriversProps {
    user: any;
}

const Drivers: React.FC<DriversProps> = ({ user }) => {
    const [searchParams] = useSearchParams();

    // State management
    const [drivers, setDrivers] = useState<Driver[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [totalCount, setTotalCount] = useState(0);
    const [categoryCount, setCategoryCount] = useState({
        // New compliance-focused counts (7 stat tiles)
        total: 0,
        clearToDrive: 0,
        notClear: 0,
        expiringLicence: 0,
        expiringCpc: 0,
        expiringTacho: 0,
        highRisk: 0,
        overdueChecks: 0,
        consentIssues: 0,
        // Legacy consent counts
        activeConsent: 0,
        pendingConsent: 0,
        noConsent: 0,
        declinedConsent: 0,
        needsAttention: 0
    });
    // Issue counts across ALL drivers (for filter dropdown) - from backend
    const [issueCounts, setIssueCounts] = useState<{ code: string; label: string; severity: IssueSeverity; count: number }[]>([]);
    // Breakdown category definitions - from backend (single source of truth for Sites page filter labels)
    const [breakdownCategories, setBreakdownCategories] = useState<BreakdownCategoryDefinition[]>([]);

    // Search and filter state - initialize from URL params
    const [searchTerm, setSearchTerm] = useState('');
    const [riskFilter, setRiskFilter] = useState(searchParams.get('risk') || 'all');
    const [statusFilter, setStatusFilter] = useState('active');
    const [siteFilter, setSiteFilter] = useState<string>(searchParams.get('site') || 'all');
    const [dueFilter, setDueFilter] = useState(searchParams.get('filter') === 'due');
    const [specialFilter, setSpecialFilter] = useState<string | null>(searchParams.get('show'));
    const [minDays, setMinDays] = useState<number | null>(searchParams.get('minDays') ? parseInt(searchParams.get('minDays')!) : null);
    const [maxDays, setMaxDays] = useState<number | null>(searchParams.get('maxDays') ? parseInt(searchParams.get('maxDays')!) : null);
    const [issueFilter, setIssueFilter] = useState<IssueFilterType>(null);
    const clearToDriveParam = searchParams.get('clearToDrive');
    const [clearToDriveFilter, setClearToDriveFilter] = useState<boolean | null>(
        clearToDriveParam === 'true' ? true : clearToDriveParam === 'false' ? false : null
    );
    const checkedTodayParam = searchParams.get('checkedToday');
    const [checkedTodayFilter, setCheckedTodayFilter] = useState<boolean>(checkedTodayParam === 'true');

    // Sorting state
    type SortField = 'name' | 'status' | 'risk' | 'site' | 'lastCheck' | 'cpcExpiry' | 'licenceNumber' | 'consent';
    const { sortBy, sortOrder, toggleSort } = useSort<SortField>('name');

    // Debounced search — delays fetch until user stops typing for 300ms
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Ref to hold the AbortController for the in-flight driver fetch
    const abortControllerRef = useRef<AbortController | null>(null);

    // Sites state
    const [sites, setSites] = useState<Site[]>([]);
    const [sitesLoading, setSitesLoading] = useState(false);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);


    // Modal states
    const [showAddDriver, setShowAddDriver] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [selectedDriverForView, setSelectedDriverForView] = useState<Driver | null>(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedDriverForEdit, setSelectedDriverForEdit] = useState<Driver | null>(null);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isLicenceCheckModalOpen, setIsLicenceCheckModalOpen] = useState(false);
    const [selectedDriverForCheck, setSelectedDriverForCheck] = useState<Driver | null>(null);
    const [isConsentModalOpen, setIsConsentModalOpen] = useState(false);
    const [selectedDriverForConsent, setSelectedDriverForConsent] = useState<Driver | null>(null);

    // Multi-select state for bulk operations
    const [selectedDriverIds, setSelectedDriverIds] = useState<Set<string>>(new Set());
    const [isSelectAllMode, setIsSelectAllMode] = useState(false);
    const [isLoadingSelection, setIsLoadingSelection] = useState(false);
    const [selectionMode, setSelectionMode] = useState<'check' | 'consent'>('check');

    // Bulk operation state
    const [isStartingBulkCheck, setIsStartingBulkCheck] = useState(false);
    const [bulkCheckResults, setBulkCheckResults] = useState<BulkOperationResultsResponse | null>(null);
    const [showResultsModal, setShowResultsModal] = useState(false);
    const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

    // Bulk consent send state
    const [isStartingBulkConsent, setIsStartingBulkConsent] = useState(false);
    const consentPollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

    // CSV import and history modal state
    const [showCSVImportModal, setShowCSVImportModal] = useState(false);
    const [showHistoryModal, setShowHistoryModal] = useState(false);

    // Deactivate / reactivate confirmation modals
    const [deactivateConfirm, setDeactivateConfirm] = useState<{ id: string; name: string } | null>(null);
    const [reactivateConfirm, setReactivateConfirm] = useState<{ id: string; name: string } | null>(null);
    const [isConfirmingDeactivate, setIsConfirmingDeactivate] = useState(false);
    const [isConfirmingReactivate, setIsConfirmingReactivate] = useState(false);

    // Consent warning modal state
    const [showConsentWarning, setShowConsentWarning] = useState(false);
    const [driversWithoutConsent, setDriversWithoutConsent] = useState<Driver[]>([]);
    const [totalSelectedForCheck, setTotalSelectedForCheck] = useState(0);

    // Recent check warning modal state
    const [showRecentCheckWarning, setShowRecentCheckWarning] = useState(false);
    const [recentlyCheckedDrivers, setRecentlyCheckedDrivers] = useState<RecentlyCheckedDriver[]>([]);
    const [driversNeedingCheck, setDriversNeedingCheck] = useState<RecentlyCheckedDriver[]>([]);

    // Consent categorization modal state
    const [showConsentCategorization, setShowConsentCategorization] = useState(false);
    const [consentCategorization, setConsentCategorization] = useState<ConsentCategorizationPreviewResponse['data'] | null>(null);

    // Issues modal state
    const [selectedDriverForIssues, setSelectedDriverForIssues] = useState<Driver | null>(null);

    // === MULTI-SELECT HANDLERS ===

    // Handle individual driver selection
    const handleDriverSelect = (driverId: string) => {
        setIsSelectAllMode(false);
        setSelectedDriverIds(prev => {
            const newSelection = new Set(prev);
            if (newSelection.has(driverId)) {
                newSelection.delete(driverId);
            } else {
                newSelection.add(driverId);
            }
            return newSelection;
        });
    };

    // Handle select all on current page
    const handleSelectAllOnPage = () => {
        if (isSelectAllMode || selectedDriverIds.size === drivers.length) {
            // Deselect all
            setSelectedDriverIds(new Set());
            setIsSelectAllMode(false);
        } else {
            // Select all on current page
            const allIds = new Set(drivers.map(d => d.id));
            setSelectedDriverIds(allIds);
            setIsSelectAllMode(false);
        }
    };

    // Clear all selection
    const handleClearSelection = () => {
        setSelectedDriverIds(new Set());
        setIsSelectAllMode(false);
        setSelectionMode('check');
    };

    // Quick select options grouped for dropdown
    const quickSelectGroups: QuickSelectGroup[] = [
        {
            label: 'By Risk Level',
            options: [
                { label: 'Select All High Risk', value: 'high_risk', description: 'High risk drivers within current filters' },
                { label: 'Select All Medium Risk', value: 'medium_risk', description: 'Medium risk drivers within current filters' },
                { label: 'Select All Low Risk', value: 'low_risk', description: 'Low risk drivers within current filters' },
            ]
        },
        {
            label: 'By Check Status',
            options: [
                { label: 'Select All Due', value: 'due', description: 'Drivers past their recheck date' },
                { label: 'Select All Never Checked', value: 'never_checked', description: 'Drivers with no DVLA check on record' },
                { label: 'Select All Critical Issues', value: 'critical_issues', description: 'Drivers who cannot legally operate' },
            ]
        },
        {
            label: 'Priority',
            options: [
                { label: 'Select All High Risk + Due', value: 'high_risk_due', description: 'Most urgent - high risk and overdue' },
                { label: 'Select All Medium Risk + Due', value: 'medium_risk_due', description: 'Medium risk and overdue' },
            ]
        },
        {
            label: 'By Consent',
            mode: 'consent',
            options: [
                { label: 'Select All No Consent', value: 'no_consent', description: 'Drivers with no consent on file' },
                { label: 'Select All Expired Consent', value: 'expired_consent', description: 'Drivers with expired consent' },
                { label: 'Select All Expiring Soon', value: 'expiring_consent', description: 'Drivers whose consent expires within 30 days' },
            ]
        },
        {
            label: 'Selection Scope',
            isFooter: true,
            options: [
                { label: 'Select All on Current Page', value: 'current_page', description: `Select all ${drivers.length} drivers on this page` },
                { label: 'Select All', value: 'all_drivers', description: 'Select every driver regardless of filters' },
                { label: 'Select All Filtered', value: 'all_filtered', description: `Select all ${totalCount} drivers matching filters` },
            ]
        }
    ];

    // Handle quick select dropdown options
    const handleQuickSelect = async (option: string) => {
        try {
            setIsLoadingSelection(true);

            // Build base filter params from current state
            const baseFilters = {
                search: debouncedSearchTerm,
                status: statusFilter,
                siteId: siteFilter === 'all' ? undefined : siteFilter,
            };

            let selectedIds: Set<string>;

            switch (option) {
                case 'high_risk': {
                    const response = await apiClient.getDrivers({
                        ...baseFilters,
                        riskLevel: 'high',
                        page: 1,
                        pageSize: totalCount || 10000,
                    });
                    selectedIds = new Set(response.data.map(d => d.id));
                    break;
                }
                case 'medium_risk': {
                    const response = await apiClient.getDrivers({
                        ...baseFilters,
                        riskLevel: 'medium',
                        page: 1,
                        pageSize: totalCount || 10000,
                    });
                    selectedIds = new Set(response.data.map(d => d.id));
                    break;
                }
                case 'low_risk': {
                    const response = await apiClient.getDrivers({
                        ...baseFilters,
                        riskLevel: 'low',
                        page: 1,
                        pageSize: totalCount || 10000,
                    });
                    selectedIds = new Set(response.data.map(d => d.id));
                    break;
                }
                case 'due': {
                    // Get drivers due for check, then intersect with current filters
                    const dueResponse = await apiClient.getDriversDueForCheck();
                    const dueIds = new Set(dueResponse.data.map(d => d.id));

                    // If we have filters applied, fetch filtered drivers and intersect
                    if (siteFilter !== 'all' || debouncedSearchTerm || riskFilter !== 'all') {
                        const filteredResponse = await apiClient.getDrivers({
                            ...baseFilters,
                            riskLevel: riskFilter === 'all' ? undefined : riskFilter,
                            page: 1,
                            pageSize: totalCount || 10000,
                        });
                        const filteredIds = new Set(filteredResponse.data.map(d => d.id));
                        selectedIds = new Set(Array.from(dueIds).filter(id => filteredIds.has(id)));
                    } else {
                        selectedIds = dueIds;
                    }
                    break;
                }
                case 'never_checked': {
                    const response = await apiClient.getDrivers({
                        ...baseFilters,
                        riskLevel: riskFilter === 'all' ? undefined : riskFilter,
                        issue: 'never_checked',
                        page: 1,
                        pageSize: totalCount || 10000,
                    });
                    selectedIds = new Set(response.data.map(d => d.id));
                    break;
                }
                case 'critical_issues': {
                    // Fetch all filtered drivers, then client-side filter for critical issues
                    // Include ALL issues with severity 'critical' - drivers who cannot legally operate
                    const response = await apiClient.getDrivers({
                        ...baseFilters,
                        riskLevel: riskFilter === 'all' ? undefined : riskFilter,
                        page: 1,
                        pageSize: totalCount || 10000,
                    });
                    // All critical issues that prevent a driver from legally operating
                    const criticalCodes = [
                        // Universal critical issues
                        'licence_invalid',
                        'licence_expired',
                        'disqualified',
                        'check_failed',
                        // Bus/Coach critical issues (PCV)
                        'missing_bus_licence',      // No D/D1
                        'minibus_only',             // D1 only - cannot drive full buses
                        'pcv_category_expired',     // D/D1 expired
                        'restricted_hire_reward',   // Restriction 101
                        'pcv_category_provisional', // Must pass full test
                        // CPC issues (Bus/Coach/Haulage)
                        'cpc_expired',
                        'cpc_missing',
                        // Haulage critical issues (HGV)
                        'missing_hgv_licence',      // No C/CE
                        'hgv_category_expired',
                        'hgv_category_provisional',
                        // Tachograph issues (Haulage/Coach)
                        'tacho_expired',
                        'tacho_missing',
                        // Car fleet critical issues
                        'missing_car_licence',      // No B
                        'car_category_expired',
                        'car_category_provisional',
                    ];
                    const criticalDrivers = response.data.filter(d =>
                        d.issues?.some(issue => criticalCodes.includes(issue.code))
                    );
                    selectedIds = new Set(criticalDrivers.map(d => d.id));
                    break;
                }
                case 'high_risk_due': {
                    // Intersection of high risk and due
                    const dueResponse = await apiClient.getDriversDueForCheck();
                    const highRiskDue = dueResponse.data.filter(d => d.riskLevel === 'high');

                    // Intersect with current filters if needed
                    if (siteFilter !== 'all' || debouncedSearchTerm) {
                        const filteredResponse = await apiClient.getDrivers({
                            ...baseFilters,
                            riskLevel: 'high',
                            page: 1,
                            pageSize: totalCount || 10000,
                        });
                        const filteredIds = new Set(filteredResponse.data.map(d => d.id));
                        selectedIds = new Set(highRiskDue.map(d => d.id).filter(id => filteredIds.has(id)));
                    } else {
                        selectedIds = new Set(highRiskDue.map(d => d.id));
                    }
                    break;
                }
                case 'medium_risk_due': {
                    // Intersection of medium risk and due
                    const dueResponse = await apiClient.getDriversDueForCheck();
                    const mediumRiskDue = dueResponse.data.filter(d => d.riskLevel === 'medium');

                    // Intersect with current filters if needed
                    if (siteFilter !== 'all' || debouncedSearchTerm) {
                        const filteredResponse = await apiClient.getDrivers({
                            ...baseFilters,
                            riskLevel: 'medium',
                            page: 1,
                            pageSize: totalCount || 10000,
                        });
                        const filteredIds = new Set(filteredResponse.data.map(d => d.id));
                        selectedIds = new Set(mediumRiskDue.map(d => d.id).filter(id => filteredIds.has(id)));
                    } else {
                        selectedIds = new Set(mediumRiskDue.map(d => d.id));
                    }
                    break;
                }
                case 'no_consent': {
                    const response = await apiClient.getDrivers({
                        ...baseFilters,
                        riskLevel: riskFilter === 'all' ? undefined : riskFilter,
                        consentStatus: 'no_consent',
                        page: 1,
                        pageSize: totalCount || 10000,
                    });
                    selectedIds = new Set(response.data.map(d => d.id));
                    break;
                }
                case 'expired_consent': {
                    const response = await apiClient.getDrivers({
                        ...baseFilters,
                        riskLevel: riskFilter === 'all' ? undefined : riskFilter,
                        consentStatus: 'expired',
                        page: 1,
                        pageSize: totalCount || 10000,
                    });
                    selectedIds = new Set(response.data.map(d => d.id));
                    break;
                }
                case 'expiring_consent': {
                    const response = await apiClient.getDrivers({
                        ...baseFilters,
                        riskLevel: riskFilter === 'all' ? undefined : riskFilter,
                        consentStatus: 'expiring_soon',
                        page: 1,
                        pageSize: totalCount || 10000,
                    });
                    selectedIds = new Set(response.data.map(d => d.id));
                    break;
                }
                case 'current_page': {
                    selectedIds = new Set(drivers.map(d => d.id));
                    break;
                }
                case 'all_drivers': {
                    const response = await apiClient.getDrivers({
                        page: 1,
                        pageSize: 10000,
                    });
                    selectedIds = new Set(response.data.map(d => d.id));
                    setIsSelectAllMode(true);
                    break;
                }
                case 'all_filtered': {
                    const response = await apiClient.getDrivers({
                        ...baseFilters,
                        riskLevel: riskFilter === 'all' ? undefined : riskFilter,
                        issue: issueFilter || undefined,
                        page: 1,
                        pageSize: totalCount || 10000,
                    });
                    selectedIds = new Set(response.data.map(d => d.id));
                    setIsSelectAllMode(true);
                    break;
                }
                default:
                    selectedIds = new Set();
            }

            setSelectedDriverIds(selectedIds);

            // Set selection mode based on the option chosen
            const consentOptions = ['no_consent', 'expired_consent', 'expiring_consent'];
            setSelectionMode(consentOptions.includes(option) ? 'consent' : 'check');

            if (option !== 'all_filtered' && option !== 'all_drivers') {
                setIsSelectAllMode(false);
            }

            // Show toast with count
            if (selectedIds.size > 0) {
                toast.success(`Selected ${selectedIds.size} driver${selectedIds.size !== 1 ? 's' : ''}`);
            } else {
                toast('No drivers match the criteria', { icon: '!' });
            }

        } catch (err) {
            console.error('Quick select failed:', err);
            toast.error('Failed to select drivers');
        } finally {
            setIsLoadingSelection(false);
        }
    };

    // === BULK CHECK FLOW HANDLERS ===

    // Poll for operation status
    const pollOperationStatus = async (operationId: string) => {
        try {
            const statusResponse = await apiClient.getBulkOperationStatus(operationId);

            if (statusResponse.data.isComplete) {
                // Stop polling
                if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                    pollingIntervalRef.current = null;
                }

                // Get the results
                const resultsResponse = await apiClient.getBulkOperationResults(operationId);
                setBulkCheckResults(resultsResponse);
                // Show toast with View Results button
                toast.success(
                    (t) => (
                        <div className="flex items-center justify-between">
                            <span className="mr-3">
                                Bulk check completed! {resultsResponse.data.operation.successCount} successful, {resultsResponse.data.operation.failureCount} failed.
                            </span>
                            <button
                                onClick={() => {
                                    setShowResultsModal(true);
                                    toast.dismiss(t.id);
                                }}
                                className="px-3 py-1 text-xs font-medium text-white rounded-md hover:opacity-90"
                                style={{ backgroundColor: '#69BE28' }}
                            >
                                View Results
                            </button>
                        </div>
                    ),
                    { duration: 15000 }
                );

                // Refresh drivers list
                await loadDrivers();
            }
        } catch (error) {
            console.error('Error polling operation status:', error);
        }
    };

    // Check for recent licence checks before proceeding
    const checkForRecentChecks = async (driverIds: string[]) => {
        try {
            const response = await apiClient.validateRecentChecks({
                driverIds,
                thresholdDays: 7
            });

            setRecentlyCheckedDrivers(response.data.recentlyCheckedDrivers);
            setDriversNeedingCheck(response.data.driversNeedingCheck);
            setShowRecentCheckWarning(true);
        } catch (err: any) {
            toast.error(err.message || 'Failed to validate recent checks');
            setIsStartingBulkCheck(false);
        }
    };

    // Actually execute the bulk check
    const proceedWithBulkCheck = async (driverIds: string[]) => {
        try {
            const response = await apiClient.startBulkLicenceCheck({ driverIds });
            const operationId = response.data.operationId;

            toast.success(`Bulk licence check started! ${response.data.totalDrivers} drivers in queue.`, {
                duration: 5000
            });

            // Start polling
            await pollOperationStatus(operationId);
            pollingIntervalRef.current = setInterval(() => {
                pollOperationStatus(operationId);
            }, 5000);

            // Clear selection
            handleClearSelection();

        } catch (err: any) {
            toast.error(err.message || 'Failed to start bulk licence check');
        } finally {
            setIsStartingBulkCheck(false);
        }
    };

    // Handle proceed from recent check warning
    const handleProceedWithSelectedDrivers = async (driverIds: string[]) => {
        setShowRecentCheckWarning(false);
        await proceedWithBulkCheck(driverIds);
    };

    // Handle proceed from consent warning
    const handleProceedFromConsentWarning = async () => {
        setShowConsentWarning(false);

        // Calculate drivers with consent by excluding those without consent
        // We already validated consent when showing the modal, so we just need to exclude the ones without
        const driverIdsWithoutConsent = new Set(driversWithoutConsent.map(d => d.id));
        const driverIdsWithConsent = Array.from(selectedDriverIds).filter(id =>
            !driverIdsWithoutConsent.has(id)
        );

        if (driverIdsWithConsent.length === 0) {
            toast.error('No drivers with valid consent to check');
            setIsStartingBulkCheck(false);
            return;
        }

        await checkForRecentChecks(driverIdsWithConsent);
    };

    // Start bulk check (validate consent first)
    const handleStartBulkCheck = async () => {
        if (selectedDriverIds.size === 0) return;

        setIsStartingBulkCheck(true);

        try {
            // For selected drivers on current page, check consent directly
            const selectedDriversOnPage = drivers.filter(d => selectedDriverIds.has(d.id));

            // If we have all selected drivers on current page, check consent directly
            if (selectedDriversOnPage.length === selectedDriverIds.size) {
                const withoutConsent = selectedDriversOnPage.filter(d => !hasValidConsent(d));

                if (withoutConsent.length > 0) {
                    setDriversWithoutConsent(withoutConsent);
                    setTotalSelectedForCheck(selectedDriversOnPage.length);
                    setIsStartingBulkCheck(false);
                    setShowConsentWarning(true);
                    return;
                }

                // All have consent, check for recent checks
                await checkForRecentChecks(selectedDriversOnPage.map(d => d.id));
            } else {
                // Need to fetch all selected drivers to check consent
                const response = await apiClient.getDrivers({
                    page: 1,
                    pageSize: selectedDriverIds.size,
                    status: 'active',
                });

                const selectedDrivers = response.data.filter(d => selectedDriverIds.has(d.id));
                const withoutConsent = selectedDrivers.filter(d => !hasValidConsent(d));

                if (withoutConsent.length > 0) {
                    setDriversWithoutConsent(withoutConsent);
                    setTotalSelectedForCheck(selectedDrivers.length);
                    setIsStartingBulkCheck(false);
                    setShowConsentWarning(true);
                    return;
                }

                // All have consent, check for recent checks
                await checkForRecentChecks(selectedDrivers.map(d => d.id));
            }
        } catch (err: any) {
            toast.error(err.message || 'Failed to start bulk check');
            setIsStartingBulkCheck(false);
        }
    };

    // === BULK CONSENT SEND HANDLERS ===

    const pollConsentOperationStatus = async (operationId: string) => {
        try {
            const statusResponse = await apiClient.getBulkOperationStatus(operationId);
            if (statusResponse.data.isComplete) {
                if (consentPollingIntervalRef.current) {
                    clearInterval(consentPollingIntervalRef.current);
                    consentPollingIntervalRef.current = null;
                }
                toast.success(
                    `Consent forms sent! ${statusResponse.data.successCount} successful, ${statusResponse.data.failureCount} failed.`,
                    { duration: 8000 }
                );
                setIsStartingBulkConsent(false);
            }
        } catch (error) {
            console.error('Error polling consent operation status:', error);
        }
    };

    const handleStartBulkConsent = async () => {
        if (selectedDriverIds.size === 0) return;
        setIsStartingBulkConsent(true);
        try {
            const driverIds = Array.from(selectedDriverIds);
            const response = await apiClient.previewConsentCategorization({
                driverIds
            });

            setConsentCategorization(response.data);
            setShowConsentCategorization(true);
        } catch (err: any) {
            toast.error(err.message || 'Failed to preview consent categorization');
        } finally {
            setIsStartingBulkConsent(false);
        }
    };

    const handleConfirmBulkConsent = async () => {
        setIsStartingBulkConsent(true);
        try {
            const driverIds = Array.from(selectedDriverIds);
            const response = await apiClient.startBulkConsentSend({
                driverIds,
                includePending: true,  // Include drivers with pending consent (send reminders)
                includeValid: false,   // Don't send to drivers with valid consent
            });
            const operationId = response.data.operationId;

            const skipped = response.data.skippedCount || 0;
            const total = response.data.totalDrivers || 0;
            toast.success(`Sending consent forms to ${total} driver${total !== 1 ? 's' : ''}${skipped > 0 ? ` (${skipped} skipped)` : ''}.`, { duration: 6000 });

            // Start polling
            await pollConsentOperationStatus(operationId);
            consentPollingIntervalRef.current = setInterval(() => {
                pollConsentOperationStatus(operationId);
            }, 5000);

            handleClearSelection();
            setShowConsentCategorization(false);
            setConsentCategorization(null);
        } catch (err: any) {
            toast.error(err.message || 'Failed to send consent forms');
        } finally {
            setIsStartingBulkConsent(false);
        }
    };

    // Handle view driver details (modal)
    const handleViewDriver = (driver: Driver) => {
        setSelectedDriverForView(driver);
        setShowViewModal(true);
    };

    const handleCloseViewModal = () => {
        setShowViewModal(false);
        setSelectedDriverForView(null);
    };

    const handleEditDriver = (driver: Driver) => {
        setSelectedDriverForEdit(driver);
        setIsEditModalOpen(true);
    };

    const handleUpdateDriver = async (driverId: string, driverData: UpdateDriverRequest) => {
        try {
            setIsUpdating(true);
            await apiClient.updateDriver(driverId, driverData);
            setIsEditModalOpen(false);
            setSelectedDriverForEdit(null);
            await loadDrivers();
        } catch (err: any) {
            throw err;
        } finally {
            setIsUpdating(false);
        }
    };

    const handleLicenceCheck = (driver: Driver) => {
        setSelectedDriverForCheck(driver);
        setIsLicenceCheckModalOpen(true);
    };

    const handleLicenceCheckComplete = async () => {
        await loadDrivers();
    };

    const handleViewConsentHistory = async (driver: Driver) => {
        // Load consent status if not already loaded
        if (!driver.consentStatus) {
            try {
                const status = await apiClient.getConsentStatus(driver.id);
                setDrivers(prev => prev.map(d =>
                    d.id === driver.id ? { ...d, consentStatus: status } : d
                ));
                setSelectedDriverForConsent({ ...driver, consentStatus: status });
            } catch (err) {
                console.error('Failed to load consent status:', err);
                setSelectedDriverForConsent(driver);
            }
        } else {
            setSelectedDriverForConsent(driver);
        }
        setIsConsentModalOpen(true);
    };

    // Load sites on component mount
    useEffect(() => {
        loadSites();
    }, []);

    // Cleanup polling interval on unmount
    useEffect(() => {
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
        };
    }, []);

    // Clear selection when filters change
    useEffect(() => {
        setSelectedDriverIds(new Set());
        setIsSelectAllMode(false);
    }, [debouncedSearchTerm, riskFilter, statusFilter, siteFilter, issueFilter, dueFilter, specialFilter, clearToDriveFilter, checkedTodayFilter, sortBy, sortOrder]);

    // Handle opening driver modal from URL parameter (e.g., from notifications)
    useEffect(() => {
        const driverId = searchParams.get('id');
        if (!driverId) return;

        // First, try to find driver in current list
        const driverInList = drivers.find(d => d.id === driverId);

        if (driverInList) {
            handleViewDriver(driverInList);
            // Remove the id parameter from URL immediately
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.delete('id');
            window.history.replaceState({}, '', `${window.location.pathname}${newSearchParams.toString() ? '?' + newSearchParams.toString() : ''}`);
        } else if (!isLoading && drivers.length > 0) {
            // Driver not in current page - fetch all drivers and try again
            const fetchSpecificDriver = async () => {
                try {
                    // Fetch drivers with no filters to find the specific driver
                    const response = await apiClient.getDrivers({
                        search: '',
                        riskLevel: 'all',
                        status: 'active',
                        page: 1,
                        pageSize: 1000, // Large page size to find the driver
                    });

                    const driver = response.data.find(d => d.id === driverId);
                    if (driver) {
                        handleViewDriver(driver);
                    } else {
                        toast.error('Driver not found');
                    }

                    // Remove the id parameter from URL
                    const newSearchParams = new URLSearchParams(searchParams);
                    newSearchParams.delete('id');
                    window.history.replaceState({}, '', `${window.location.pathname}${newSearchParams.toString() ? '?' + newSearchParams.toString() : ''}`);
                } catch (err) {
                    console.error('Error fetching driver:', err);
                    toast.error('Failed to load driver details');
                    // Still remove the parameter even if there's an error
                    const newSearchParams = new URLSearchParams(searchParams);
                    newSearchParams.delete('id');
                    window.history.replaceState({}, '', `${window.location.pathname}${newSearchParams.toString() ? '?' + newSearchParams.toString() : ''}`);
                }
            };

            fetchSpecificDriver();
        }
    }, [searchParams, drivers, isLoading]);


    // Sync filter state from URL params when navigating to page
    useEffect(() => {
        const riskParam = searchParams.get('risk');
        if (riskParam && ['low', 'medium', 'high'].includes(riskParam)) {
            setRiskFilter(riskParam);
        }

        // Handle ?filter=due parameter
        const filterParam = searchParams.get('filter');
        setDueFilter(filterParam === 'due');

        // Handle ?site= parameter
        const siteParam = searchParams.get('site');
        if (siteParam) {
            setSiteFilter(siteParam);
        }

        // Handle ?show= parameter for special filters
        const showParam = searchParams.get('show');
        setSpecialFilter(showParam);

        // Handle ?minDays= and ?maxDays= parameters for expiry date filtering
        const minDaysParam = searchParams.get('minDays');
        const maxDaysParam = searchParams.get('maxDays');
        setMinDays(minDaysParam ? parseInt(minDaysParam) : null);
        setMaxDays(maxDaysParam ? parseInt(maxDaysParam) : null);

        // Handle ?clearToDrive= parameter
        const ctdParam = searchParams.get('clearToDrive');
        setClearToDriveFilter(ctdParam === 'true' ? true : ctdParam === 'false' ? false : null);

        // Handle ?checkedToday= parameter
        const checkedParam = searchParams.get('checkedToday');
        setCheckedTodayFilter(checkedParam === 'true');
    }, [searchParams]);

    // Load drivers on component mount and when filters change.
    // Cancels the previous in-flight request before firing a new one.
    useEffect(() => {
        abortControllerRef.current?.abort();
        abortControllerRef.current = new AbortController();
        loadDrivers(abortControllerRef.current.signal);
        return () => { abortControllerRef.current?.abort(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearchTerm, riskFilter, statusFilter, siteFilter, currentPage, dueFilter, specialFilter, minDays, maxDays, issueFilter, clearToDriveFilter, checkedTodayFilter, sortBy, sortOrder]);

    const loadSites = async () => {
        try {
            setSitesLoading(true);
            const response = await apiClient.getSites({ active: true, pageSize: 1000 });
            setSites(response.data);
        } catch (err) {
            console.error('Error loading sites:', err);
        } finally {
            setSitesLoading(false);
        }
    };

    const loadDrivers = async (signal?: AbortSignal) => {
        try {
            setIsLoading(true);
            setError(null);

            // If filtering by drivers due for check, use the dedicated endpoint
            if (dueFilter) {
                const dueResponse = await apiClient.getDriversDueForCheck(signal);
                // Map the due drivers data to match Driver interface
                const dueDrivers: Driver[] = dueResponse.data.map(d => ({
                    id: d.id,
                    firstName: d.firstName,
                    lastName: d.lastName,
                    licenceNumber: d.licenceNumber,
                    riskLevel: d.riskLevel as 'low' | 'medium' | 'high',
                    lastLicenceCheck: d.lastCheckDate,
                    email: '',
                    consentProvided: true, // These drivers have consent (otherwise wouldn't be in due list)
                    active: true,
                    daysSinceLastCheck: d.daysSinceLastCheck
                } as Driver & { daysSinceLastCheck: number }));

                // Apply local filters if any
                let filteredDrivers = dueDrivers;
                if (searchTerm) {
                    const term = searchTerm.toLowerCase();
                    filteredDrivers = filteredDrivers.filter(d =>
                        d.firstName.toLowerCase().includes(term) ||
                        d.lastName.toLowerCase().includes(term) ||
                        d.licenceNumber.toLowerCase().includes(term)
                    );
                }
                if (riskFilter !== 'all') {
                    filteredDrivers = filteredDrivers.filter(d => d.riskLevel === riskFilter);
                }

                setDrivers(filteredDrivers);
                setTotalCount(filteredDrivers.length);
                // Due filter endpoint doesn't return full counts - use filtered count for total
                setCategoryCount(prev => ({
                    ...prev,
                    total: filteredDrivers.length,
                }));
                // Due filter endpoint doesn't return issue counts - clear them
                setIssueCounts([]);
            } else {
                // Check if we have a special filter with a site - use backend filtering
                if (specialFilter && siteFilter && siteFilter !== 'all') {
                    // Fetch matching driver IDs from backend (accurate filtering)
                    const filterResponse = await apiClient.getSiteRiskFilteredDrivers(
                        siteFilter,
                        specialFilter,
                        signal
                    );
                    const matchingDriverIds = new Set(filterResponse.data);

                    // Fetch all drivers for the site
                    const response = await apiClient.getDrivers({
                        search: searchTerm,
                        riskLevel: riskFilter,
                        status: statusFilter,
                        siteId: siteFilter,
                        page: 1,
                        pageSize: 1000,
                        signal,
                    });

                    // Filter to only matching drivers
                    const filteredDrivers = response.data.filter(d => matchingDriverIds.has(d.id));
                    setDrivers(filteredDrivers);
                    setTotalCount(filteredDrivers.length);
                    setCategoryCount(response.counts);
                    setIssueCounts(response.issueCounts || []);
                    setBreakdownCategories(response.breakdownCategories || []);
                } else {
                    // Standard driver fetch
                    const response = await apiClient.getDrivers({
                        search: searchTerm,
                        riskLevel: riskFilter,
                        status: statusFilter,
                        siteId: siteFilter === 'all' ? undefined : siteFilter,
                        issue: specialFilter || issueFilter || undefined,
                        clearToDrive: clearToDriveFilter ?? undefined,
                        checkedToday: checkedTodayFilter || undefined,
                        minDays: minDays ?? undefined,
                        maxDays: maxDays ?? undefined,
                        sortBy,
                        sortOrder,
                        page: currentPage,
                        pageSize: pageSize,
                        signal,
                    });

                    // When filtering by issue, use response data directly (already filtered by backend)
                    setDrivers(response.data);
                    setTotalCount(response.totalCount);
                    setCategoryCount(response.counts);
                    setIssueCounts(response.issueCounts || []);
                    setBreakdownCategories(response.breakdownCategories || []);
                }
            }
        } catch (err: any) {
            // Ignore aborted requests — not a real error
            if (err.name === 'AbortError') return;
            setError(err.message || 'Failed to load drivers');
            console.error('Error loading drivers:', err);
        } finally {
            // Only clear loading state if this request wasn't cancelled
            if (!signal?.aborted) {
                setIsLoading(false);
            }
        }
    };

    const handleCreateDriver = async (driverData: CreateDriverRequest) => {
        try {
            setIsCreating(true);
            setError(null);

            await apiClient.createDriver(driverData);

            setShowAddDriver(false);
            await loadDrivers();

        } catch (err: any) {
            setError(err.message || 'Failed to create driver');
            throw err;
        } finally {
            setIsCreating(false);
        }
    };

    const handleDeactivateDriver = (driverId: string, driverName: string) => {
        setDeactivateConfirm({ id: driverId, name: driverName });
    };

    const confirmDeactivateDriver = async () => {
        if (!deactivateConfirm) return;
        try {
            setIsConfirmingDeactivate(true);
            setError(null);
            await apiClient.deactivateDriver(deactivateConfirm.id);
            toast.success(`${deactivateConfirm.name} has been deactivated`);
            setDeactivateConfirm(null);
            await loadDrivers();
        } catch (err: any) {
            setError(err.message || 'Failed to deactivate driver');
        } finally {
            setIsConfirmingDeactivate(false);
        }
    };

    const handleReactivateDriver = (driverId: string, driverName: string) => {
        setReactivateConfirm({ id: driverId, name: driverName });
    };

    const confirmReactivateDriver = async () => {
        if (!reactivateConfirm) return;
        try {
            setIsConfirmingReactivate(true);
            setError(null);
            const response = await apiClient.reactivateDriver(reactivateConfirm.id);
            toast.success(response.message || 'Driver reactivated successfully');
            setReactivateConfirm(null);
            await loadDrivers();
        } catch (err: any) {
            const errorMessage = err.message || 'Failed to reactivate driver';
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setIsConfirmingReactivate(false);
        }
    };

    const handleStatusFilter = (value: string) => {
        setStatusFilter(value);
        setCurrentPage(1);
    };

    const handleSearch = (value: string) => {
        setSearchTerm(value);
        setCurrentPage(1);
    };

    const handleRiskFilter = (value: string) => {
        setRiskFilter(value);
        setCurrentPage(1);
    };

    const handleSiteFilter = (value: string) => {
        setSiteFilter(value);
        setCurrentPage(1);
    };

    const handleIssueFilter = (filter: IssueFilterType) => {
        if (issueFilter === filter) {
            setIssueFilter(null); // Toggle off if same filter
        } else {
            setIssueFilter(filter);
        }
        setCurrentPage(1);
    };

    // Get filter label dynamically from first driver with that issue (no manual mapping needed)
    const getIssueFilterLabel = (filter: IssueFilterType): string => {
        if (!filter) return '';
        // Find any driver with this issue and get the filterLabel from backend
        for (const driver of drivers) {
            const issue = driver.issues?.find(i => i.code === filter);
            if (issue) return issue.filterLabel;
        }
        return filter; // Fallback to code if not found
    };

    const getConsentStatusDisplay = (driver: Driver) => {
        // Check for withdrawn consent first
        if (driver.consentStatus?.tokens && driver.consentStatus.tokens.length > 0) {
            const latestToken = driver.consentStatus.tokens[0];
            if (latestToken.status === 'withdrawn') {
                return {
                    status: 'Declined',
                    color: 'text-orange-800',
                    bgColor: 'bg-orange-100',
                    icon: XCircle,
                    canSendRequest: true
                };
            }
        }

        // Check for active consent
        if (driver.consentProvided && driver.consentExpiry) {
            const expiryDate = new Date(driver.consentExpiry);
            const now = new Date();
            const thirtyDaysFromNow = new Date(now.getTime() + (30 * 24 * 60 * 60 * 1000));

            if (expiryDate < now) {
                return {
                    status: 'Expired',
                    color: 'text-red-800',
                    bgColor: 'bg-red-100',
                    icon: XCircle,
                    canSendRequest: true
                };
            } else if (expiryDate < thirtyDaysFromNow) {
                return {
                    status: 'Expires Soon',
                    color: 'text-yellow-800',
                    bgColor: 'bg-yellow-100',
                    icon: AlertTriangle,
                    canSendRequest: false
                };
            } else {
                return {
                    status: 'Active',
                    color: 'text-green-800',
                    bgColor: 'bg-green-100',
                    icon: CheckCircle,
                    canSendRequest: false
                };
            }
        }

        // Check if we have token status information (pending consent request)
        if (!driver.consentProvided && driver.consentStatus?.tokens && driver.consentStatus.tokens.length > 0) {
            const latestToken = driver.consentStatus.tokens[0];

            // Check token status
            switch (latestToken.status) {
                case 'sent':
                    return {
                        status: 'Sent',
                        color: 'text-blue-800',
                        bgColor: 'bg-blue-100',
                        icon: Clock,
                        canSendRequest: false
                    };
                case 'opened':
                    return {
                        status: 'Opened',
                        color: 'text-purple-800',
                        bgColor: 'bg-purple-100',
                        icon: Clock,
                        canSendRequest: false
                    };
                case 'expired':
                    return {
                        status: 'Link Expired',
                        color: 'text-orange-800',
                        bgColor: 'bg-orange-100',
                        icon: XCircle,
                        canSendRequest: true
                    };
                default:
                    // For other statuses, check if token is still valid
                    if (latestToken.expiresAt && new Date(latestToken.expiresAt) > new Date() &&
                        latestToken.status !== 'withdrawn' && latestToken.status !== 'expired') {
                        return {
                            status: 'Pending',
                            color: 'text-blue-800',
                            bgColor: 'bg-blue-100',
                            icon: Clock,
                            canSendRequest: false
                        };
                    }
            }
        }

        // No consent and no pending request
        return {
            status: 'No Consent',
            color: 'text-gray-800',
            bgColor: 'bg-gray-100',
            icon: Clock,
            canSendRequest: true
        };
    };

    const totalPages = Math.ceil(totalCount / pageSize);

    return (
        <div className="max-w-7xl mx-auto px-4 py-8">

            {/* Print Header */}
            <div className="hidden print:block border-b-2 pb-4 mb-8" style={{ borderColor: '#231f20' }}>
                <div className="flex items-center justify-between">
                    <img
                        src="/logo-horizontal-black.png"
                        alt="IntelliDrive"
                        className="h-20 w-auto"
                    />
                    <div className="text-right">
                        <h1 className="text-lg font-bold text-gray-900">
                            IntelliDrive Compliance Dashboard
                        </h1>
                        <p className="text-sm text-gray-600">
                            Generated: {new Date().toLocaleDateString('en-GB')}
                        </p>
                    </div>
                </div>
            </div>

            {/* Header */}
            <div className="mb-6">
                <div className="flex items-start justify-between mb-2">
                    <div className="w-1/2 sm:flex-1">
                        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Drivers</h1>
                    </div>
                    <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                        <button
                            onClick={() => setShowHistoryModal(true)}
                            className="p-2 sm:inline-flex sm:items-center sm:px-4 sm:py-2 text-gray-700 bg-white text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300"
                            title="View History"
                        >
                            <History className="w-4 h-4 sm:mr-2" />
                            <span className="hidden sm:inline">History</span>
                        </button>
                        <button
                            onClick={() => setShowCSVImportModal(true)}
                            disabled={user?.organisation?.id === '9435251e-0099-4ddf-acad-dc893186419c' || user?.organisation?.id === '2ad339da-8608-4936-a5e7-942b5cf67b9d' || user?.organisation?.id === '4afe1ce3-60d8-4ea5-b315-cf82e5be1b34'}
                            className={`p-2 sm:inline-flex sm:items-center sm:px-4 sm:py-2 text-sm font-medium rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 ${
                                user?.organisation?.id === '9435251e-0099-4ddf-acad-dc893186419c' || user?.organisation?.id === '2ad339da-8608-4936-a5e7-942b5cf67b9d' || user?.organisation?.id === '4afe1ce3-60d8-4ea5-b315-cf82e5be1b34'
                                    ? 'text-gray-400 bg-gray-100 cursor-not-allowed opacity-60'
                                    : 'text-gray-700 bg-white hover:bg-gray-50 transition-colors'
                            }`}
                            title="Import CSV"
                        >
                            <Upload className="w-4 h-4 sm:mr-2" />
                            <span className="hidden sm:inline">Import CSV</span>
                        </button>
                        <button
                            onClick={() => setShowAddDriver(true)}
                            className="p-2 sm:inline-flex sm:items-center sm:px-4 sm:py-2 text-white text-sm font-medium rounded-md hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-offset-2"
                            style={{ backgroundColor: '#69BE28', '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                            title="Add Driver"
                        >
                            <Plus className="w-4 h-4 sm:mr-2" />
                            <span className="hidden sm:inline">Add Driver</span>
                        </button>
                    </div>
                </div>
                <p className="text-sm sm:text-base text-gray-600 w-3/4 sm:w-full">
                    Manage your driver database and compliance records
                </p>
            </div>

            {/* Compliance Stat Tiles - 7 tiles in single row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
                {/* Total Drivers */}
                <div
                    onClick={() => {
                        setClearToDriveFilter(null);
                        setIssueFilter(null);
                        setRiskFilter('all');
                    }}
                    className={`bg-white rounded-lg shadow p-3 text-left transition-all hover:shadow-md cursor-pointer relative group ${
                        clearToDriveFilter === null && issueFilter === null && riskFilter === 'all'
                            ? 'ring-2 ring-gray-400'
                            : ''
                    }`}
                >
                    <div className="flex items-center">
                        <Users className="w-5 h-5 text-gray-500 flex-shrink-0" />
                        <div className="ml-2 min-w-0 flex-1">
                            <div className="flex items-center gap-1">
                                <p className="text-xs font-medium text-gray-500 truncate">Total</p>
                                <Info className="w-3 h-3 text-gray-300" />
                            </div>
                            <p className="text-lg font-bold text-gray-900">{categoryCount.total}</p>
                        </div>
                    </div>
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-48 bg-gray-800 text-white text-xs rounded-lg p-2 hidden group-hover:block z-50 shadow-lg">
                        Total active drivers in your fleet. Click to clear all filters.
                    </div>
                </div>

                {/* Clear to Drive */}
                <div
                    onClick={() => {
                        if (clearToDriveFilter === true) {
                            setClearToDriveFilter(null);
                        } else {
                            setClearToDriveFilter(true);
                            setIssueFilter(null);
                            setRiskFilter('all');
                        }
                    }}
                    className={`bg-white rounded-lg shadow p-3 text-left transition-all hover:shadow-md cursor-pointer relative group ${
                        clearToDriveFilter === true
                            ? 'ring-2 ring-green-500'
                            : ''
                    }`}
                >
                    <div className="flex items-center">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                        <div className="ml-2 min-w-0 flex-1">
                            <div className="flex items-center gap-1">
                                <p className="text-xs font-medium text-gray-500 truncate">Clear to Drive</p>
                                <Info className="w-3 h-3 text-gray-300" />
                            </div>
                            <p className="text-lg font-bold text-gray-900">{categoryCount.clearToDrive}</p>
                        </div>
                    </div>
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-56 bg-gray-800 text-white text-xs rounded-lg p-2 hidden group-hover:block z-50 shadow-lg">
                        <p className="font-semibold mb-1">Safe to operate</p>
                        <p className="text-gray-300">Drivers with a valid licence check and no critical compliance issues. These drivers can legally work.</p>
                    </div>
                </div>

                {/* Not Clear */}
                <div
                    onClick={() => {
                        if (clearToDriveFilter === false) {
                            setClearToDriveFilter(null);
                        } else {
                            setClearToDriveFilter(false);
                            setIssueFilter(null);
                            setRiskFilter('all');
                        }
                    }}
                    className={`bg-white rounded-lg shadow p-3 text-left transition-all hover:shadow-md cursor-pointer relative group ${
                        clearToDriveFilter === false
                            ? 'ring-2 ring-red-500'
                            : ''
                    }`}
                >
                    <div className="flex items-center">
                        <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                        <div className="ml-2 min-w-0 flex-1">
                            <div className="flex items-center gap-1">
                                <p className="text-xs font-medium text-gray-500 truncate">Not Clear</p>
                                <Info className="w-3 h-3 text-gray-300" />
                            </div>
                            <p className="text-lg font-bold text-gray-900">{categoryCount.notClear}</p>
                        </div>
                    </div>
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-56 bg-gray-800 text-white text-xs rounded-lg p-2 hidden group-hover:block z-50 shadow-lg">
                        <p className="font-semibold mb-1 text-red-300">Cannot operate</p>
                        <p className="text-gray-300">Drivers with critical issues (revoked licence, disqualified, expired documents). Must not drive until resolved.</p>
                    </div>
                </div>

                {/* Expiring Soon - with mini badges when there are expiring items */}
                <div className="bg-white rounded-lg shadow p-3 text-left relative group">
                    <div className="flex items-center">
                        <Clock className="w-5 h-5 text-yellow-600 flex-shrink-0" />
                        <div className="ml-2 min-w-0 flex-1">
                            <div className="flex items-center gap-1">
                                <p className="text-xs font-medium text-gray-500 truncate">Expiring Soon</p>
                                <Info className="w-3 h-3 text-gray-300" />
                            </div>
                            <p className="text-lg font-bold text-gray-900">
                                {categoryCount.expiringLicence + categoryCount.expiringCpc + categoryCount.expiringTacho}
                            </p>
                        </div>
                    </div>
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-60 bg-gray-800 text-white text-xs rounded-lg p-2 hidden group-hover:block z-50 shadow-lg">
                        <p className="font-semibold mb-1 text-yellow-300">Action required soon</p>
                        <p className="text-gray-300">Documents expiring within 30 days. Renew before expiry to maintain compliance:</p>
                        <ul className="mt-1 text-gray-300 list-disc list-inside">
                            <li>Driving licence</li>
                            <li>Driver CPC card (PCV/HGV)</li>
                            <li>Digital tachograph card</li>
                        </ul>
                    </div>
                    {/* Mini badges for breakdown - only show when there are expiring items */}
                    {(categoryCount.expiringLicence + categoryCount.expiringCpc + categoryCount.expiringTacho) > 0 && (
                        <div className="flex gap-1 flex-wrap mt-2">
                            {categoryCount.expiringLicence > 0 && (
                                <button
                                    onClick={() => {
                                        if (issueFilter === 'licence_expiring') {
                                            setIssueFilter(null);
                                        } else {
                                            setClearToDriveFilter(null);
                                            setIssueFilter('licence_expiring');
                                            setRiskFilter('all');
                                        }
                                    }}
                                    className={`inline-flex items-center px-1.5 py-0.5 text-xs rounded transition-all hover:bg-yellow-100 ${
                                        issueFilter === 'licence_expiring' ? 'bg-yellow-200 ring-1 ring-yellow-500' : 'bg-yellow-50'
                                    }`}
                                    title="Licence expiring soon"
                                >
                                    <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mr-1"></span>
                                    Licence: {categoryCount.expiringLicence}
                                </button>
                            )}
                            {categoryCount.expiringCpc > 0 && (
                                <button
                                    onClick={() => {
                                        if (issueFilter === 'cpc_expiring') {
                                            setIssueFilter(null);
                                        } else {
                                            setClearToDriveFilter(null);
                                            setIssueFilter('cpc_expiring');
                                            setRiskFilter('all');
                                        }
                                    }}
                                    className={`inline-flex items-center px-1.5 py-0.5 text-xs rounded transition-all hover:bg-yellow-100 ${
                                        issueFilter === 'cpc_expiring' ? 'bg-yellow-200 ring-1 ring-yellow-500' : 'bg-yellow-50'
                                    }`}
                                    title="CPC card expiring soon"
                                >
                                    <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mr-1"></span>
                                    CPC: {categoryCount.expiringCpc}
                                </button>
                            )}
                            {categoryCount.expiringTacho > 0 && (
                                <button
                                    onClick={() => {
                                        if (issueFilter === 'tacho_expiring') {
                                            setIssueFilter(null);
                                        } else {
                                            setClearToDriveFilter(null);
                                            setIssueFilter('tacho_expiring');
                                            setRiskFilter('all');
                                        }
                                    }}
                                    className={`inline-flex items-center px-1.5 py-0.5 text-xs rounded transition-all hover:bg-yellow-100 ${
                                        issueFilter === 'tacho_expiring' ? 'bg-yellow-200 ring-1 ring-yellow-500' : 'bg-yellow-50'
                                    }`}
                                    title="Tacho card expiring soon"
                                >
                                    <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mr-1"></span>
                                    Tacho: {categoryCount.expiringTacho}
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {/* High Risk */}
                <div
                    onClick={() => {
                        if (riskFilter === 'high') {
                            setRiskFilter('all');
                        } else {
                            setClearToDriveFilter(null);
                            setIssueFilter(null);
                            setRiskFilter('high');
                        }
                    }}
                    className={`bg-white rounded-lg shadow p-3 text-left transition-all hover:shadow-md cursor-pointer relative group ${
                        riskFilter === 'high'
                            ? 'ring-2 ring-orange-500'
                            : ''
                    }`}
                >
                    <div className="flex items-center">
                        <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0" />
                        <div className="ml-2 min-w-0 flex-1">
                            <div className="flex items-center gap-1">
                                <p className="text-xs font-medium text-gray-500 truncate">High Risk</p>
                                <Info className="w-3 h-3 text-gray-300" />
                            </div>
                            <p className="text-lg font-bold text-gray-900">{categoryCount.highRisk}</p>
                        </div>
                    </div>
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-56 bg-gray-800 text-white text-xs rounded-lg p-2 hidden group-hover:block z-50 shadow-lg">
                        <p className="font-semibold mb-1 text-orange-300">Prioritise for review</p>
                        <p className="text-gray-300">Drivers with endorsements, points, or other issues that increase risk. May need more frequent checks or additional training.</p>
                    </div>
                </div>

                {/* Overdue Checks */}
                <div
                    onClick={() => {
                        if (issueFilter === 'overdue') {
                            setIssueFilter(null);
                        } else {
                            setClearToDriveFilter(null);
                            setIssueFilter('overdue');
                            setRiskFilter('all');
                        }
                    }}
                    className={`bg-white rounded-lg shadow p-3 text-left transition-all hover:shadow-md cursor-pointer relative group ${
                        issueFilter === 'overdue'
                            ? 'ring-2 ring-blue-500'
                            : ''
                    }`}
                >
                    <div className="flex items-center">
                        <RotateCw className="w-5 h-5 text-blue-600 flex-shrink-0" />
                        <div className="ml-2 min-w-0 flex-1">
                            <div className="flex items-center gap-1">
                                <p className="text-xs font-medium text-gray-500 truncate">Overdue Checks</p>
                                <Info className="w-3 h-3 text-gray-300" />
                            </div>
                            <p className="text-lg font-bold text-gray-900">{categoryCount.overdueChecks}</p>
                        </div>
                    </div>
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-56 bg-gray-800 text-white text-xs rounded-lg p-2 hidden group-hover:block z-50 shadow-lg">
                        <p className="font-semibold mb-1 text-blue-300">Recheck required</p>
                        <p className="text-gray-300">Drivers past their scheduled licence recheck date. Run a new check to verify their licence status is still valid.</p>
                    </div>
                </div>

                {/* Consent Issues */}
                <div
                    onClick={() => {
                        if (issueFilter === 'no_consent') {
                            setIssueFilter(null);
                        } else {
                            setClearToDriveFilter(null);
                            setIssueFilter('no_consent');
                            setRiskFilter('all');
                        }
                    }}
                    className={`bg-white rounded-lg shadow p-3 text-left transition-all hover:shadow-md cursor-pointer relative group ${
                        issueFilter === 'no_consent'
                            ? 'ring-2 ring-gray-500'
                            : ''
                    }`}
                >
                    <div className="flex items-center">
                        <FileText className="w-5 h-5 text-gray-500 flex-shrink-0" />
                        <div className="ml-2 min-w-0 flex-1">
                            <div className="flex items-center gap-1">
                                <p className="text-xs font-medium text-gray-500 truncate">Consent Issues</p>
                                <Info className="w-3 h-3 text-gray-300" />
                            </div>
                            <p className="text-lg font-bold text-gray-900">{categoryCount.consentIssues}</p>
                        </div>
                    </div>
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-56 bg-gray-800 text-white text-xs rounded-lg p-2 hidden group-hover:block z-50 shadow-lg">
                        <p className="font-semibold mb-1">GDPR consent required</p>
                        <p className="text-gray-300">Drivers who haven't provided consent for DVLA licence checks. Send consent requests to enable automated compliance monitoring.</p>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="mb-6 bg-white p-4 rounded-lg shadow">
                <div className="flex flex-col sm:flex-row gap-4">
                    {/* Search */}
                    <div className="flex-1 relative">
                        <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by name, licence number, or email..."
                            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2"
                            style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                            value={searchTerm}
                            onChange={(e) => handleSearch(e.target.value)}
                        />
                    </div>

                    {/* Site Filter */}
                    <div className="sm:w-48">
                        <select
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2"
                            style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                            value={siteFilter}
                            onChange={(e) => handleSiteFilter(e.target.value)}
                            disabled={sitesLoading}
                        >
                            <option value="all">All Sites</option>
                            <option value="unassigned">Unassigned</option>
                            {sites.map(site => (
                                <option key={site.id} value={site.id}>
                                    {site.name} ({site.code})
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Risk Filter */}
                    <div className="sm:w-48">
                        <select
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2"
                            style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                            value={riskFilter}
                            onChange={(e) => handleRiskFilter(e.target.value)}
                        >
                            <option value="all">All Risk Levels</option>
                            <option value="low">Low Risk</option>
                            <option value="medium">Medium Risk</option>
                            <option value="high">High Risk</option>
                        </select>
                    </div>

                    {/* Status Filter */}
                    <div className="sm:w-48">
                        <select
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2"
                            style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                            value={statusFilter}
                            onChange={(e) => handleStatusFilter(e.target.value)}
                        >
                            <option value="active">Active Drivers</option>
                            <option value="all">All Drivers</option>
                            <option value="inactive">Inactive Drivers</option>
                        </select>
                    </div>

                    {/* Issue Filter Dropdown - uses backend-aggregated counts across ALL drivers */}
                    <div className="sm:w-56">
                        <select
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2"
                            style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                            value={issueFilter || ''}
                            onChange={(e) => setIssueFilter(e.target.value || null)}
                        >
                            <option value="">All Issues</option>
                            {issueCounts
                                .sort((a, b) => {
                                    // Sort by severity (critical first), then by count
                                    const severityOrder: Record<string, number> = { critical: 0, warning: 1, info: 2 };
                                    const severityDiff = (severityOrder[a.severity] || 2) - (severityOrder[b.severity] || 2);
                                    if (severityDiff !== 0) return severityDiff;
                                    return b.count - a.count;
                                })
                                .map(issue => (
                                    <option key={issue.code} value={issue.code}>
                                        {issue.label} ({issue.count})
                                    </option>
                                ))
                            }
                        </select>
                    </div>
                </div>
            </div>

            {/* Due Filter Banner */}
            {dueFilter && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between">
                    <div className="flex items-center">
                        <Clock className="w-4 h-4 text-amber-600 mr-2" />
                        <span className="text-sm font-medium text-amber-800">
                            Showing drivers due for licence checks ({totalCount} {totalCount === 1 ? 'driver' : 'drivers'})
                        </span>
                    </div>
                    <button
                        onClick={() => {
                            setDueFilter(false);
                            // Remove filter param from URL
                            const newSearchParams = new URLSearchParams(searchParams);
                            newSearchParams.delete('filter');
                            window.history.replaceState({}, '', `${window.location.pathname}${newSearchParams.toString() ? '?' + newSearchParams.toString() : ''}`);
                        }}
                        className="text-sm font-medium text-amber-700 hover:text-amber-900"
                    >
                        Clear filter
                    </button>
                </div>
            )}

            {/* Special Filter Banner (from Site Risk clicks) */}
            {specialFilter && (() => {
                // Handle combined "all" filters with custom labels
                const allFilterLabels: Record<string, string> = {
                    'all_expired': 'any expired document',
                    'all_expiring': 'any document expiring soon',
                };
                // Dynamic lookup from backend definitions (single source of truth)
                const category = breakdownCategories.find(c => c.filter === specialFilter);
                const label = allFilterLabels[specialFilter] || category?.label || specialFilter;
                const daysText = maxDays ? ` (${(minDays || 0) + 1}-${maxDays} days)` : '';
                return (
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
                        <div className="flex items-center">
                            <Filter className="w-4 h-4 text-blue-600 mr-2" />
                            <span className="text-sm font-medium text-blue-800">
                                Showing drivers with {label}{daysText} ({totalCount})
                            </span>
                        </div>
                        <button
                            onClick={() => {
                                setSpecialFilter(null);
                                setMinDays(null);
                                setMaxDays(null);
                                // Remove show, minDays, maxDays, and site params from URL
                                const newSearchParams = new URLSearchParams(searchParams);
                                newSearchParams.delete('show');
                                newSearchParams.delete('minDays');
                                newSearchParams.delete('maxDays');
                                newSearchParams.delete('site');
                                window.history.replaceState({}, '', `${window.location.pathname}${newSearchParams.toString() ? '?' + newSearchParams.toString() : ''}`);
                            }}
                            className="text-sm font-medium text-blue-700 hover:text-blue-900"
                        >
                            Clear filter
                        </button>
                    </div>
                );
            })()}

            {/* Issue Filter Banner (from clicking issue badges) */}
            {issueFilter && (
                <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg flex items-center justify-between">
                    <div className="flex items-center">
                        <Filter className="w-4 h-4 text-purple-600 mr-2" />
                        <span className="text-sm font-medium text-purple-800">
                            Filtering by: {getIssueFilterLabel(issueFilter)} ({totalCount} {totalCount === 1 ? 'driver' : 'drivers'})
                        </span>
                    </div>
                    <button
                        onClick={() => setIssueFilter(null)}
                        className="text-sm font-medium text-purple-700 hover:text-purple-900"
                    >
                        Clear filter
                    </button>
                </div>
            )}

            {/* Clear to Drive Filter Banner */}
            {clearToDriveFilter !== null && (
                <div className={`mb-4 p-3 rounded-lg flex items-center justify-between ${
                    clearToDriveFilter
                        ? 'bg-green-50 border border-green-200'
                        : 'bg-red-50 border border-red-200'
                }`}>
                    <div className="flex items-center">
                        <Filter className={`w-4 h-4 mr-2 ${clearToDriveFilter ? 'text-green-600' : 'text-red-600'}`} />
                        <span className={`text-sm font-medium ${clearToDriveFilter ? 'text-green-800' : 'text-red-800'}`}>
                            Showing: {clearToDriveFilter ? 'Clear to Drive' : 'Not Clear to Drive'} ({totalCount} {totalCount === 1 ? 'driver' : 'drivers'})
                        </span>
                    </div>
                    <button
                        onClick={() => setClearToDriveFilter(null)}
                        className={`text-sm font-medium ${clearToDriveFilter ? 'text-green-700 hover:text-green-900' : 'text-red-700 hover:text-red-900'}`}
                    >
                        Clear filter
                    </button>
                </div>
            )}

            {/* Checked Today Filter Banner */}
            {checkedTodayFilter && (
                <div className="mb-4 p-3 rounded-lg flex items-center justify-between bg-blue-50 border border-blue-200">
                    <div className="flex items-center">
                        <Filter className="w-4 h-4 mr-2 text-blue-600" />
                        <span className="text-sm font-medium text-blue-800">
                            Showing: Checked Today ({totalCount} {totalCount === 1 ? 'driver' : 'drivers'})
                        </span>
                    </div>
                    <button
                        onClick={() => setCheckedTodayFilter(false)}
                        className="text-sm font-medium text-blue-700 hover:text-blue-900"
                    >
                        Clear filter
                    </button>
                </div>
            )}

            {/* Results Summary */}
            <div className="mb-4 flex justify-between items-center">
                <p className="text-sm text-gray-600">
                    Showing {drivers.length} of {totalCount} drivers
                </p>
                {searchTerm && (
                    <button
                        onClick={() => handleSearch('')}
                        className="text-sm hover:opacity-80 transition-opacity"
                        style={{ color: '#69BE28' }}
                    >
                        Clear search
                    </button>
                )}
            </div>

            {/* Error Display */}
            {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
                    <div className="flex items-center">
                        <AlertTriangle className="w-4 h-4 text-red-500 mr-2" />
                        <span className="text-red-800">{error}</span>
                    </div>
                </div>
            )}

            {/* Drivers Table */}
            <div className="bg-white shadow rounded-lg overflow-hidden">
                {isLoading ? (
                    <div className="p-8 text-center">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: '#69BE28' }} />
                        <p className="mt-2 text-gray-600">Loading drivers...</p>
                    </div>
                ) : drivers.length === 0 ? (
                    <div className="p-8 text-center">
                        <Users className="w-12 h-12 mx-auto text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">No drivers found</h3>
                        <p className="mt-1 text-sm text-gray-500">
                            {searchTerm ? 'Try adjusting your search criteria.' : 'Get started by adding your first driver.'}
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    {/* Checkbox column for multi-select */}
                                    <th className="px-3 py-3 text-left w-10">
                                        <input
                                            type="checkbox"
                                            ref={(el) => {
                                                if (el) {
                                                    el.indeterminate = !isSelectAllMode &&
                                                        selectedDriverIds.size > 0 &&
                                                        selectedDriverIds.size < drivers.length;
                                                }
                                            }}
                                            checked={isSelectAllMode || (selectedDriverIds.size === drivers.length && drivers.length > 0)}
                                            onChange={handleSelectAllOnPage}
                                            className="w-4 h-4 rounded border-gray-300 cursor-pointer"
                                            style={{ accentColor: '#69BE28' }}
                                            title="Select all on this page"
                                        />
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        <button
                                            onClick={() => toggleSort('name')}
                                            className="flex items-center hover:text-gray-900"
                                        >
                                            Driver
                                            {sortBy === 'name' && (sortOrder === 'asc' ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />)}
                                        </button>
                                    </th>
                                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        <button
                                            onClick={() => toggleSort('status')}
                                            className="flex items-center justify-center w-full hover:text-gray-900"
                                            title="Sort by clear to drive status"
                                        >
                                            Status
                                            {sortBy === 'status' && (sortOrder === 'asc' ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />)}
                                        </button>
                                    </th>
                                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        <button
                                            onClick={() => toggleSort('risk')}
                                            className="flex items-center justify-center w-full hover:text-gray-900"
                                        >
                                            Risk
                                            {sortBy === 'risk' && (sortOrder === 'asc' ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />)}
                                        </button>
                                    </th>
                                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        <button
                                            onClick={() => toggleSort('consent')}
                                            className="flex items-center justify-center w-full hover:text-gray-900"
                                        >
                                            Consent
                                            {sortBy === 'consent' && (sortOrder === 'asc' ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />)}
                                        </button>
                                    </th>
                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        <button
                                            onClick={() => toggleSort('site')}
                                            className="flex items-center hover:text-gray-900"
                                        >
                                            Site
                                            {sortBy === 'site' && (sortOrder === 'asc' ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />)}
                                        </button>
                                    </th>
                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        <button
                                            onClick={() => toggleSort('lastCheck')}
                                            className="flex items-center hover:text-gray-900"
                                        >
                                            Last Check
                                            {sortBy === 'lastCheck' && (sortOrder === 'asc' ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />)}
                                        </button>
                                    </th>
                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        <button
                                            onClick={() => toggleSort('cpcExpiry')}
                                            className="flex items-center hover:text-gray-900"
                                        >
                                            CPC Expiry
                                            {sortBy === 'cpcExpiry' && (sortOrder === 'asc' ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />)}
                                        </button>
                                    </th>
                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        <button
                                            onClick={() => toggleSort('licenceNumber')}
                                            className="flex items-center hover:text-gray-900"
                                        >
                                            Licence Number
                                            {sortBy === 'licenceNumber' && (sortOrder === 'asc' ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />)}
                                        </button>
                                    </th>
                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {drivers.map((driver) => (
                                    <tr
                                        key={driver.id}
                                        className={`hover:bg-gray-50 transition-colors ${selectedDriverIds.has(driver.id) ? 'bg-blue-50' : ''}`}
                                    >
                                        {/* Checkbox cell for multi-select */}
                                        <td className="px-3 py-4">
                                            <input
                                                type="checkbox"
                                                checked={selectedDriverIds.has(driver.id)}
                                                onChange={() => handleDriverSelect(driver.id)}
                                                className="w-4 h-4 rounded border-gray-300 cursor-pointer"
                                                style={{ accentColor: '#69BE28' }}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap">
                                            <div>
                                                <div className="text-sm font-medium text-gray-900">
                                                    {driver.firstName} {driver.lastName}
                                                </div>
                                                <div className="text-sm text-gray-500">{driver.email}</div>
                                            </div>
                                        </td>
                                        <td className="px-3 py-4 whitespace-nowrap text-center">
                                            {(() => {
                                                const issues = driver.issues || [];
                                                const hasIssues = issues.length > 0;

                                                // Pending - no check performed yet
                                                if (driver.clearToDrive === null || driver.clearToDrive === undefined) {
                                                    return (
                                                        <span
                                                            className="inline-block w-6 h-6 rounded-full bg-gray-200 text-gray-500 text-xs font-medium flex items-center justify-center"
                                                            title="Pending licence check"
                                                        >
                                                            ?
                                                        </span>
                                                    );
                                                }

                                                // Clear to drive with no issues - green checkmark
                                                if (driver.clearToDrive === true && !hasIssues) {
                                                    return (
                                                        <span
                                                            className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-500 text-white"
                                                            title="Clear to drive - no issues"
                                                        >
                                                            <Check className="w-4 h-4" />
                                                        </span>
                                                    );
                                                }

                                                // Has issues - show clickable indicator that opens modal
                                                if (hasIssues) {
                                                    const worstSeverity = issues.reduce((worst, issue) => {
                                                        if (issue.severity === 'critical') return 'critical';
                                                        if (issue.severity === 'warning' && worst !== 'critical') return 'warning';
                                                        return worst;
                                                    }, 'info' as 'critical' | 'warning' | 'info');

                                                    const severityConfig = {
                                                        critical: { bg: 'bg-red-500', text: 'text-white' },
                                                        warning: { bg: 'bg-yellow-500', text: 'text-white' },
                                                        info: { bg: 'bg-blue-500', text: 'text-white' }
                                                    };
                                                    const config = severityConfig[worstSeverity];

                                                    return (
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedDriverForIssues(driver);
                                                            }}
                                                            className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${config.bg} ${config.text} hover:opacity-80 transition-opacity cursor-pointer animate-attention-pulse`}
                                                            title={`${issues.length} issue${issues.length !== 1 ? 's' : ''}. Click to view details.`}
                                                        >
                                                            {issues.length}
                                                        </button>
                                                    );
                                                }

                                                // Not clear to drive but no specific issues (edge case)
                                                return (
                                                    <span
                                                        className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-500 text-white"
                                                        title="Not clear to drive"
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </span>
                                                );
                                            })()}
                                        </td>
                                        <td className="px-3 py-4 whitespace-nowrap text-center">
                                            {(() => {
                                                const risk = driver.riskLevel?.toLowerCase();
                                                if (risk === 'high') {
                                                    return <span className="inline-block w-3 h-3 rounded-full bg-red-500" title="High risk" />;
                                                } else if (risk === 'medium') {
                                                    return <span className="inline-block w-3 h-3 rounded-full bg-yellow-500" title="Medium risk" />;
                                                } else if (risk === 'low') {
                                                    return <span className="inline-block w-3 h-3 rounded-full bg-green-500" title="Low risk" />;
                                                } else {
                                                    return <span className="inline-block w-3 h-3 rounded-full bg-gray-300" title="Unknown risk" />;
                                                }
                                            })()}
                                        </td>
                                        <td className="px-3 py-4 whitespace-nowrap text-center">
                                            {(() => {
                                                const consentDisplay = getConsentStatusDisplay(driver);
                                                const status = consentDisplay.status;

                                                // Icon-only display with tooltip
                                                if (status === 'Active') {
                                                    return (
                                                        <span
                                                            className="inline-block w-3 h-3 rounded-full bg-green-500"
                                                            title="Active consent"
                                                        />
                                                    );
                                                } else if (status === 'Expires Soon') {
                                                    return (
                                                        <span
                                                            className="inline-block w-3 h-3 rounded-full bg-yellow-500"
                                                            title="Consent expires soon"
                                                        />
                                                    );
                                                } else if (status === 'Sent') {
                                                    return (
                                                        <span
                                                            className="inline-block w-3 h-3 rounded-full bg-gray-300"
                                                            title="Consent request sent - awaiting response"
                                                        />
                                                    );
                                                } else {
                                                    // Expired, Declined, No Consent
                                                    return (
                                                        <span
                                                            className="inline-block w-3 h-3 rounded-full bg-red-500"
                                                            title={`${status} - no valid consent`}
                                                        />
                                                    );
                                                }
                                            })()}
                                        </td>
                                        <td className="px-3 py-4 whitespace-nowrap">
                                            <div className="flex items-center text-sm text-gray-900" title={driver.site ? `${driver.site.name} (${driver.site.code})` : 'Unassigned'}>
                                                {driver.site ? (
                                                    <>
                                                        <Building2 className="w-3 h-3 mr-1 text-gray-400 flex-shrink-0" />
                                                        <span className="truncate max-w-[120px]">{driver.site.name}</span>
                                                    </>
                                                ) : (
                                                    <span className="text-gray-400 italic text-xs">Unassigned</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {driver.lastLicenceCheck ? (
                                                <div>
                                                    <div>{new Date(driver.lastLicenceCheck).toLocaleDateString()}</div>
                                                    <div className="text-xs text-gray-400">
                                                        {new Date(driver.lastLicenceCheck).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                    </div>
                                                </div>
                                            ) : (
                                                'Never'
                                            )}
                                        </td>
                                        <td className="px-3 py-4 whitespace-nowrap text-sm">
                                            {driver.cpcExpiryDate ? (
                                                (() => {
                                                    const expiryDate = new Date(driver.cpcExpiryDate);
                                                    const now = new Date();
                                                    const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
                                                    const isExpired = expiryDate < now;
                                                    const isExpiringSoon = !isExpired && expiryDate < thirtyDaysFromNow;

                                                    return (
                                                        <span className={`${isExpired ? 'text-red-600 font-medium' : isExpiringSoon ? 'text-yellow-600 font-medium' : 'text-gray-500'}`}>
                                                            {expiryDate.toLocaleDateString()}
                                                            {isExpired && <span className="ml-1 text-xs">(Expired)</span>}
                                                            {isExpiringSoon && <span className="ml-1 text-xs">(Soon)</span>}
                                                        </span>
                                                    );
                                                })()
                                            ) : (
                                                <span className="text-gray-400 italic">N/A</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {driver.licenceNumber}
                                        </td>
                                        <td className="px-3 py-4 whitespace-nowrap text-sm font-medium">
                                            <div className="flex space-x-1.5">
                                                <button
                                                    onClick={() => handleViewDriver(driver)}
                                                    className="hover:opacity-80 transition-opacity"
                                                    style={{ color: '#69BE28' }}
                                                    title="View Driver Details"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleLicenceCheck(driver)}
                                                    className="text-purple-600 hover:text-purple-900"
                                                    title="Check DVLA Licence"
                                                >
                                                    <Shield className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleViewConsentHistory(driver)}
                                                    className="text-blue-600 hover:text-blue-900"
                                                    title="View Consent History"
                                                >
                                                    <FileText className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleEditDriver(driver)}
                                                    className="text-green-600 hover:text-green-900"
                                                    title="Edit Driver"
                                                >
                                                    <Edit className="w-4 h-4" />
                                                </button>
                                                {driver.active ? (
                                                    <button
                                                        onClick={() => handleDeactivateDriver(driver.id, `${driver.firstName} ${driver.lastName}`)}
                                                        className="text-red-600 hover:text-red-900"
                                                        title="Deactivate Driver"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={() => handleReactivateDriver(driver.id, `${driver.firstName} ${driver.lastName}`)}
                                                        className="text-blue-600 hover:text-blue-900"
                                                        title="Reactivate Driver"
                                                    >
                                                        <RotateCw className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="mt-6 flex justify-between items-center">
                    <div className="text-sm text-gray-600">
                        Page {currentPage} of {totalPages}
                    </div>
                    <div className="flex space-x-2">
                        <button
                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                            className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Previous
                        </button>
                        <button
                            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                            disabled={currentPage === totalPages}
                            className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}

            {/* Add Driver Modal */}
            <AddDriverModal
                isOpen={showAddDriver}
                onClose={() => setShowAddDriver(false)}
                onSubmit={handleCreateDriver}
                isCreating={isCreating}
            />

            {/* View Driver Modal */}
            <DriverViewModal
                driver={selectedDriverForView}
                isOpen={showViewModal}
                onClose={handleCloseViewModal}
                onDriverUpdated={loadDrivers}
                subtype={user?.organisation?.subtype}
            />

            {/* Edit Driver Modal */}
            <EditDriverModal
                isOpen={isEditModalOpen}
                onClose={() => {
                    setIsEditModalOpen(false);
                    setSelectedDriverForEdit(null);
                }}
                onSubmit={handleUpdateDriver}
                isUpdating={isUpdating}
                driver={selectedDriverForEdit}
            />

            {/* Licence Check Modal */}
            <LicenceCheckModal
                isOpen={isLicenceCheckModalOpen}
                onClose={() => {
                    setIsLicenceCheckModalOpen(false);
                    setSelectedDriverForCheck(null);
                }}
                driver={selectedDriverForCheck}
                onCheckComplete={handleLicenceCheckComplete}
                subtype={user?.organisation?.subtype}
            />

            {/* Consent Details Modal */}
            {isConsentModalOpen && selectedDriverForConsent && (
                <ConsentDetailsModal
                    driver={selectedDriverForConsent}
                    onClose={() => {
                        setIsConsentModalOpen(false);
                        setSelectedDriverForConsent(null);
                    }}
                    onRefresh={loadDrivers}
                />
            )}

            {/* === MULTI-SELECT BULK CHECK COMPONENTS === */}

            {/* Selection Toolbar - floating at bottom when drivers selected */}
            <SelectionToolbar
                selectedCount={selectedDriverIds.size}
                totalFilteredCount={totalCount}
                isSelectAllMode={isSelectAllMode}
                isLoading={isLoadingSelection}
                isStartingOperation={selectionMode === 'consent' ? isStartingBulkConsent : isStartingBulkCheck}
                selectionMode={selectionMode}
                onClearSelection={handleClearSelection}
                onQuickSelect={handleQuickSelect}
                onStartBulkCheck={handleStartBulkCheck}
                onStartBulkConsent={handleStartBulkConsent}
                quickSelectGroups={quickSelectGroups}
            />

            {/* Bulk Check Results Modal */}
            <BulkCheckResultsModal
                isOpen={showResultsModal}
                onClose={() => setShowResultsModal(false)}
                results={bulkCheckResults}
                subtype={user?.organisation?.subtype}
            />

            {/* Consent Warning Modal */}
            <ConsentWarningModal
                isOpen={showConsentWarning}
                onClose={() => {
                    setShowConsentWarning(false);
                    setIsStartingBulkCheck(false);
                }}
                onConfirm={handleProceedFromConsentWarning}
                onCancel={() => {
                    setShowConsentWarning(false);
                    setDriversWithoutConsent([]);
                    setTotalSelectedForCheck(0);
                    setIsStartingBulkCheck(false);
                }}
                isConfirming={isStartingBulkCheck}
                driversWithoutConsent={driversWithoutConsent}
                totalSelectedDrivers={totalSelectedForCheck}
            />

            {/* CSV Import Modal */}
            <CSVImportModal
                isOpen={showCSVImportModal}
                onClose={() => setShowCSVImportModal(false)}
                onImportComplete={() => {
                    setShowCSVImportModal(false);
                    loadDrivers();
                }}
            />

            {/* Drivers History Modal */}
            <DriversHistoryModal
                isOpen={showHistoryModal}
                onClose={() => setShowHistoryModal(false)}
                subtype={user?.organisation?.subtype}
            />

            {/* Recent Check Warning Modal */}
            <RecentCheckWarningModal
                isOpen={showRecentCheckWarning}
                onClose={() => {
                    setShowRecentCheckWarning(false);
                    setIsStartingBulkCheck(false);
                }}
                recentlyCheckedDrivers={recentlyCheckedDrivers}
                driversNeedingCheck={driversNeedingCheck}
                onProceed={handleProceedWithSelectedDrivers}
                thresholdDays={7}
            />

            {/* Consent Categorization Modal */}
            <ConsentCategorizationModal
                isOpen={showConsentCategorization}
                onClose={() => {
                    setShowConsentCategorization(false);
                    setConsentCategorization(null);
                }}
                categorization={consentCategorization}
                onProceed={handleConfirmBulkConsent}
                isLoading={isStartingBulkConsent}
            />

            {/* Deactivate Driver Confirmation Modal */}
            {deactivateConfirm && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={() => !isConfirmingDeactivate && setDeactivateConfirm(null)} />
                        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                            <div className="flex items-center space-x-3 mb-4">
                                <div className="p-2 bg-red-100 rounded-full">
                                    <Trash2 className="w-5 h-5 text-red-600" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">Deactivate Driver</h3>
                            </div>
                            <p className="text-sm text-gray-600 mb-1">
                                Are you sure you want to deactivate <span className="font-medium text-gray-900">{deactivateConfirm.name}</span>?
                            </p>
                            <p className="text-sm text-gray-500 mb-6">
                                They will no longer appear in active driver lists and automated checks will be skipped.
                            </p>
                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={() => setDeactivateConfirm(null)}
                                    disabled={isConfirmingDeactivate}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmDeactivateDriver}
                                    disabled={isConfirmingDeactivate}
                                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center space-x-2"
                                >
                                    {isConfirmingDeactivate && <Loader2 className="w-4 h-4 animate-spin" />}
                                    <span>{isConfirmingDeactivate ? 'Deactivating...' : 'Deactivate'}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Reactivate Driver Confirmation Modal */}
            {reactivateConfirm && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={() => !isConfirmingReactivate && setReactivateConfirm(null)} />
                        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                            <div className="flex items-center space-x-3 mb-4">
                                <div className="p-2 bg-blue-100 rounded-full">
                                    <RotateCw className="w-5 h-5 text-blue-600" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">Reactivate Driver</h3>
                            </div>
                            <p className="text-sm text-gray-600 mb-1">
                                Are you sure you want to reactivate <span className="font-medium text-gray-900">{reactivateConfirm.name}</span>?
                            </p>
                            <p className="text-sm text-gray-500 mb-6">
                                They will be added back to active driver lists and will need to provide consent again before checks can be performed.
                            </p>
                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={() => setReactivateConfirm(null)}
                                    disabled={isConfirmingReactivate}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmReactivateDriver}
                                    disabled={isConfirmingReactivate}
                                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
                                >
                                    {isConfirmingReactivate && <Loader2 className="w-4 h-4 animate-spin" />}
                                    <span>{isConfirmingReactivate ? 'Reactivating...' : 'Reactivate'}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Issues Modal */}
            {selectedDriverForIssues && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        {/* Backdrop */}
                        <div
                            className="fixed inset-0 bg-black/50 transition-opacity"
                            onClick={() => setSelectedDriverForIssues(null)}
                        />

                        {/* Modal */}
                        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                            {/* Header */}
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900">
                                        Driver Issues
                                    </h3>
                                    <p className="text-sm text-gray-500">
                                        {selectedDriverForIssues.firstName} {selectedDriverForIssues.lastName}
                                    </p>
                                </div>
                                <button
                                    onClick={() => setSelectedDriverForIssues(null)}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            {/* Issues List */}
                            <div className="space-y-2 max-h-80 overflow-y-auto">
                                {(selectedDriverForIssues.issues || []).map((issue, index) => {
                                    const IconComponent = iconComponents[issue.icon] || AlertTriangle;
                                    const severityStyles = {
                                        critical: 'bg-red-50 border-red-200 text-red-800',
                                        warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
                                        info: 'bg-blue-50 border-blue-200 text-blue-800'
                                    };
                                    const iconStyles = {
                                        critical: 'text-red-500',
                                        warning: 'text-yellow-500',
                                        info: 'text-blue-500'
                                    };

                                    return (
                                        <button
                                            key={issue.code || index}
                                            onClick={() => {
                                                handleIssueFilter(issue.code);
                                                setSelectedDriverForIssues(null);
                                            }}
                                            className={`w-full flex items-start gap-3 p-3 rounded-lg border ${severityStyles[issue.severity]} hover:opacity-80 transition-opacity text-left`}
                                        >
                                            <IconComponent className={`w-5 h-5 flex-shrink-0 mt-0.5 ${iconStyles[issue.severity]}`} />
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-sm">{issue.filterLabel || issue.label}</p>
                                                <p className="text-xs opacity-75 mt-0.5">
                                                    Click to filter all drivers with this issue
                                                </p>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Footer */}
                            <div className="mt-4 pt-4 border-t flex justify-between items-center">
                                <span className="text-sm text-gray-500">
                                    {selectedDriverForIssues.clearToDrive ? (
                                        <span className="text-yellow-600">Clear to drive (warnings only)</span>
                                    ) : (
                                        <span className="text-red-600">Not clear to drive</span>
                                    )}
                                </span>
                                <button
                                    onClick={() => {
                                        setSelectedDriverForView(selectedDriverForIssues);
                                        setShowViewModal(true);
                                        setSelectedDriverForIssues(null);
                                    }}
                                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                                >
                                    View full profile →
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Drivers;